<script setup lang="ts">
const props = defineProps({
  attribute: {
    default: '',
    type: String as PropType<
      | 'apps'
      | 'contacts'
      | 'socials'
      | `locations.headquarters.${string}`
      | CompanyV2Attribute
    >,
  },
  company: {
    default: undefined,
    type: Object as PropType<CompanyV2>,
  },
  moreResults: {
    default: false,
    type: Boolean,
  },
})

const { t } = useI18n()
const guards = useGuards()

async function handleClick() {
  await guards.awaitUser({
    onClose: () => {},
    onSuccess: () => {},
    title: props.attribute ? 'datapoints' : 'moreResults',
  })
}
</script>

<template>
  <div class="flex w-full items-start">
    <ClientOnly>
      <UPopover
        mode="hover"
        :popper="{
          placement: 'bottom',
          adaptive: true,
        }"
        :text="t('tooltip')"
      >
        <UButton
          class="group/button"
          size="xs"
          @click="handleClick"
        >
          <span class="flex items-center gap-1.5">
            <div class="relative flex size-4 items-center justify-center">
              <UIcon
                class="spring-bounce-10 spring-duration-100 absolute inset-0 size-4 text-indigo-500 group-hover/button:scale-50 group-hover/button:opacity-0 dark:text-indigo-500"
                :name="getIcon('locked')"
              />

              <UIcon
                class="spring-bounce-10 spring-duration-100 absolute inset-0 size-4 scale-50 text-indigo-500 opacity-0 group-hover/button:scale-110 group-hover/button:opacity-100 dark:text-indigo-500"
                :name="getIcon('unlocked')"
              />
            </div>

            <template v-if="attribute">
              {{ t('unlock', { attribute: t(`datapoints.attributes.${attribute.replaceAll('.', '-')}`) }) }}
            </template>

            <template v-else>
              {{ t('moreResults') }}
            </template>
          </span>
        </UButton>

        <template #panel>
          <div class="p-4">
            <p class="w-full truncate text-left font-normal sm:text-base">
              <template v-if="attribute">
                <span class="flex items-center gap-1.5">
                  <AnimateRadiantText>
                    {{ t('tooltipDatapoints') }}
                  </AnimateRadiantText>

                  <span class="flex items-center gap-1 text-indigo-500">
                    <UAvatar
                      size="xs"
                      :src="getLogo(company?.domain?.domain, 'companies')"
                    />

                    <span>
                      {{ company?.domain?.domain }}
                    </span>
                  </span>
                </span>
              </template>

              <template v-else>
                <AnimateRadiantText>
                  {{ t('tooltipMoreResults') }}
                </AnimateRadiantText>
              </template>
            </p>
          </div>
        </template>
      </UPopover>
    </ClientOnly>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "moreResults": "Unlock more results",
    "tooltipDatapoints": "Login to unlock 300+ datapoints on",
    "tooltipMoreResults": "Login to unlock all available results for this search.",
    "unlock": "Unlock {attribute}"
  },
  "es": {
    "moreResults": "Desbloquear más resultados",
    "tooltipDatapoints": "Inicia sesión para desbloquear 300+ puntos de datos en",
    "tooltipMoreResults": "Inicia sesión para desbloquear todos los resultados disponibles para esta búsqueda.",
    "unlock": "Desbloquear {attribute}"
  },
  "fr": {
    "moreResults": "Débloquer plus de résultats",
    "tooltipDatapoints": "Connectez-vous pour débloquer 300+ points de données sur",
    "tooltipMoreResults": "Connectez-vous pour débloquer tous les résultats disponibles pour cette recherche.",
    "unlock": "Débloquer {attribute}"
  }
}
</i18n>
